import { useState, useEffect, useMemo, useRef } from 'react'
import { getFieldReports, getProjectLocations, getAllProjectForms, getFieldReportTemplate, getProjectAssets } from '../services/projectService'
import { checkFormAccess } from '../services/formPermissionsService'
import { useAuth } from '../contexts/AuthContext'
import { useProject } from '../contexts/ProjectContext'
import { useToast } from '../contexts/ToastContext'
import { autodeskApiThrottler } from '../utils/requestThrottler'
import axios from 'axios'
import ResizableHeader from './ResizableHeader'
import { LocationDropdown } from './LocationComponents'
import { FIELD_IDS, FUEL_TYPE_OPTIONS, DEFAULT_FIELD_NAME_MAP, getFieldValue } from './FieldValueComponents'
import { useSuccessModal, SuccessModal } from './SuccessModal'
import { useConfirmModal, ConfirmModal } from './ConfirmModal'
import LoadingIndicator from './LoadingIndicator'
import SkeletonLoader from './SkeletonLoader'
import AttachmentModal from './AttachmentModal'
import ViewInACCButton from './ViewInACCButton'
import ComplianceStatus from './ComplianceStatus'
import Button from './Button'
import LoadingOverlay from './LoadingOverlay'
import ActionsColumn from './ActionsColumn'
import WorkflowHistory from './WorkflowHistory'
import AttachmentsColumn from './AttachmentsColumn'
import PackagesColumn from './PackagesColumn'
import FuelLogFieldColumns from './FuelLogFieldColumns'
import TemplateTable from './TemplateTable'
import { PROJECT_IDS, isCostManagementProject, TEMPLATE_FIELD_CONFIGS } from '../config/projects'
import { sortData, sortLocations } from '../utils/sortUtils'
import { toggleItemSelection, selectAllItems, deselectAllItems } from '../utils/selectionUtils'
import { buildLocationMap } from '../utils/dataTransformUtils'
import { parseTemplateFields, createFieldNameMap, getFieldValueFromReport, formatFieldValue } from '../utils/templateFieldParser'
import { useCostWorkflows } from '../hooks/useCostWorkflows'
import { useFuelLogOperations } from '../hooks/useFuelLogOperations'
import { useContractOperations } from '../hooks/useContractOperations'
import { useAttachmentOperations } from '../hooks/useAttachmentOperations'
import '../styles/ApprovalSummary.css'
import '../styles/ApprovalSummaryInline.css'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3003'

// Helper function to check if viewing Cost Module
const isCostModuleView = (projectId) => {
  return projectId === PROJECT_IDS.COST_MODULE
}

const ApprovalSummaryTable = ({ projectId, templateId, isAuthenticated }) => {
  const { token, user } = useAuth()
  const { allProjectsForms, allProjectsLoadingProgress, projects, config } = useProject()
  const { showSuccess, showError, showInfo } = useToast()

  // success modal (using hook) - moved up so it can be used in fuel log operations hook
  const successModal = useSuccessModal()

  // confirm modal for approval/rejection confirmations
  const confirmModal = useConfirmModal()

  // Ref to hold fetchStats function for fuel log operations
  const fetchStatsRef = useRef(null)

  const [stats, setStats] = useState({ total: 0, pendingApproval: 0, completed: 0, open: 0 })
  // Initialize loading=true to show skeleton loaders immediately
  // This prevents "No forms awaiting" from flashing before data loads
  const [loading, setLoading] = useState(true)
  const [pendingReports, setPendingReports] = useState([])
  const [sortKey, setSortKey] = useState('createdAt')
  const [sortDir, setSortDir] = useState('desc')

  // Template field definitions { templateId: { fields: [], fieldMap: {} } }
  const [templateFieldDefs, setTemplateFieldDefs] = useState({})

  // Template permissions { templateId: { canView, canEdit, canApprove, canReject, hasReviewerAccess } }
  const [templatePermissions, setTemplatePermissions] = useState({})

  // Cost management project permissions (single project-level permission with roleIds)
  const [costProjectPermissions, setCostProjectPermissions] = useState(null)

  // Use cost workflows hook for cost management projects (includes both contracts and subcontracts)
  const costWorkflowsHook = useCostWorkflows(projectId, user, token, costProjectPermissions)

  // selection state
  const [selectedMap, setSelectedMap] = useState({}) // { [reportId]: true }

  // per-row UI state
  const [rowLoading, setRowLoading] = useState({})      // { [reportId]: true/false }
  const [rowAction, setRowAction] = useState({})        // { [reportId]: 'approve'/'reject'/'save' }
  const [rowError, setRowError] = useState({})          // { [reportId]: 'message' }
  const [rowSuccess, setRowSuccess] = useState({})      // { [reportId]: 'message' }

  // edit mode state
  const [editingRow, setEditingRow] = useState(null)    // reportId of row being edited
  const [editedValues, setEditedValues] = useState({})  // { [fieldId]: newValue }
  const [editingFieldIds, setEditingFieldIds] = useState([]) // Field IDs for the form being edited

  // location dropdown state
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false)
  const [expandedLocations, setExpandedLocations] = useState({}) // { [locationId]: true/false }
  const locationTriggerRef = useRef(null)

  // search/filter state
  const [searchQuery, setSearchQuery] = useState('') // Search query for Asset/Location filtering

  // column resizing state
  const [columnWidths, setColumnWidths] = useState({})
  const [resizingColumn, setResizingColumn] = useState(null)
  const [startX, setStartX] = useState(0)
  const [startWidth, setStartWidth] = useState(0)

  // Sticky header state
  const [isHeaderSticky, setIsHeaderSticky] = useState(false)
  const tableRef = useRef(null)
  const theadRef = useRef(null)

  // Attachments and packages data for each contract
  const [contractAttachments, setContractAttachments] = useState({}) // { contractId: attachments[] }
  const [contractPackages, setContractPackages] = useState({}) // { contractId: packages[] }
  const [contractWorkflowHistory, setContractWorkflowHistory] = useState({}) // { contractId: history[] }
  const [contractPendingApprovals, setContractPendingApprovals] = useState({}) // { contractId: pendingTasks[] }

  // Use attachment operations hook
  const {
    selectedAttachment,
    showAttachmentModal,
    downloadingAttachments,
    handleOpenAttachment,
    handleCloseAttachmentModal,
    handleDownloadAttachment
  } = useAttachmentOperations({
    projectId,
    token,
    showInfo,
    showSuccess,
    showError
  })

  // Location mapping (locationId -> locationName)
  const [locationMap, setLocationMap] = useState({})
  // Full location data with order and hierarchy info
  const [locationsList, setLocationsList] = useState([])
  // Loading state for locations
  const [loadingLocations, setLoadingLocations] = useState(false)
  // Asset mapping (equipment number / ca1 -> clientAssetId) for Equipment Yard asset matching
  const [assetMap, setAssetMap] = useState({})
  // Direct form->asset mapping (formId -> clientAssetId) from ACC Relationships API
  const [formAssetMap, setFormAssetMap] = useState({})

  // Field ID to name mapping (initialized from defaults, merged with API data)
  const [fieldNameMap, setFieldNameMap] = useState(DEFAULT_FIELD_NAME_MAP);

  // Merge API field labels into the map
  useEffect(() => {
    if (pendingReports.length === 0) return
    const map = { ...fieldNameMap }
    pendingReports.forEach((report) => {
      const fields = report.fields || []
      fields.forEach(field => {
        const fieldId = field.id || field.fieldId
        const fieldLabel = field.label || field.name || field.displayName
        if (fieldId && fieldLabel && !map[fieldId]) map[fieldId] = fieldLabel
      })
    })
    setFieldNameMap(map)
  }, [pendingReports])

  // Sync cost workflows hook state to component state for cost management projects
  useEffect(() => {
    // Skip for All Projects view - it uses allProjectsForms from context instead
    if (isCostModuleView(projectId)) return

    if (!isCostManagementProject(projectId)) return

    // Sync hook's loading state to component loading state
    setLoading(costWorkflowsHook.loading)

    // Sync data when hook has finished loading
    if (!costWorkflowsHook.loading && costWorkflowsHook.workflows.length >= 0) {
      setStats(costWorkflowsHook.stats)
      setPendingReports(costWorkflowsHook.workflows) // Now includes both contracts and subcontracts
      setContractAttachments(costWorkflowsHook.attachments)
      setContractPackages(costWorkflowsHook.packages)
      setContractWorkflowHistory(costWorkflowsHook.workflowHistory)
      setContractPendingApprovals(costWorkflowsHook.pendingApprovals)
    }
  }, [
    projectId,
    costWorkflowsHook.loading,
    costWorkflowsHook.stats,
    costWorkflowsHook.workflows,
    costWorkflowsHook.attachments,
    costWorkflowsHook.packages,
    costWorkflowsHook.workflowHistory,
    costWorkflowsHook.pendingApprovals
  ])

  // Fetch cost project permissions for role-based assignment matching
  useEffect(() => {
    const fetchCostPermissions = async () => {
      // Skip for All Projects view
      if (isCostModuleView(projectId)) return

      if (!isCostManagementProject(projectId) || !projectId || !token) return

      try {
        const permissions = await checkFormAccess(projectId, null)
        setCostProjectPermissions(permissions)
      } catch (error) {
        console.error('[ApprovalSummary] Error fetching cost project permissions:', error)
        setCostProjectPermissions(null)
      }
    }

    fetchCostPermissions()
  }, [projectId, token])

  // LAZY LOADING: Fetch metadata on-demand instead of upfront
  // This function will be called when user needs to view attachments/packages
  const fetchMetadataForForm = async (form) => {
    const sourceProjectId = form._sourceProjectId
    const formId = form.id || form.formId || form.reportId

    if (!sourceProjectId || !formId || !token) return

    // Check if already loaded
    if (form._metadataLoaded) return

    console.log(`[ApprovalSummary] Lazy loading metadata for form ${formId}...`)

    try {
      const isChangeOrder = form._isChangeOrder || form._changeOrderType

      // Fetch all metadata in parallel
      const [attachmentRes, packagesRes, historyRes] = await Promise.all([
        // Attachments
        axios.get(
          isChangeOrder
            ? `${API_BASE_URL}/api/cost/${sourceProjectId}/change-orders/${formId}/attachments`
            : `${API_BASE_URL}/api/cost/${sourceProjectId}/contracts/${formId}/attachments`,
          {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            withCredentials: true
          }
        ).catch(() => ({ data: { success: false, attachments: [] } })),

        // Packages
        axios.get(
          isChangeOrder
            ? `${API_BASE_URL}/api/cost/${sourceProjectId}/change-orders/${formId}/packages`
            : `${API_BASE_URL}/api/cost/${sourceProjectId}/contracts/${formId}/packages`,
          {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            withCredentials: true
          }
        ).catch(() => ({ data: { success: false, packages: [] } })),

        // Workflow history (only for contracts)
        !isChangeOrder ? axios.get(
          `${API_BASE_URL}/api/cost/${sourceProjectId}/contracts/${formId}/workflow-history`,
          {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            withCredentials: true
          }
        ).catch(() => ({ data: { success: false, history: [] } })) : Promise.resolve({ data: { success: false, history: [] } })
      ])

      // Update state with fetched metadata
      setContractAttachments(prev => ({
        ...prev,
        [formId]: attachmentRes.data.success ? (attachmentRes.data.attachments || []) : []
      }))

      setContractPackages(prev => ({
        ...prev,
        [formId]: packagesRes.data.success ? (packagesRes.data.packages || []) : []
      }))

      setContractWorkflowHistory(prev => ({
        ...prev,
        [formId]: historyRes.data.success ? (historyRes.data.history || []) : []
      }))

      console.log(`[ApprovalSummary] ✓ Metadata loaded for form ${formId}`)
    } catch (error) {
      console.error('[ApprovalSummary] Error lazy loading metadata:', error)
    }
  }

  // Expose fetchMetadataForForm for use in table components
  // This will be called when user expands/views a row
  window.fetchMetadataForForm = fetchMetadataForForm

  // Use fuel log operations hook for field reports
  const fuelLogOps = useFuelLogOperations({
    projectId,
    templateId,
    onRefresh: () => fetchStatsRef.current?.(),
    onError: (msg) => successModal.open(msg, 5000, 'reject'),
    onSuccess: (msg, type) => successModal.open(msg, type === 'reject' ? 3000 : undefined, type),
    setRowLoading,
    setRowError,
    setRowSuccess,
    setRowAction,
    setLoading,
    templateFieldDefs  // Pass template field definitions for field type detection
  })

  // Use contract operations hook for subcontracts
  const contractOps = useContractOperations({
    projectId,
    token,
    onRefresh: () => fetchStatsRef.current?.(),
    onError: (msg) => successModal.open(msg, 5000, 'reject'),
    onSuccess: (msg, type) => successModal.open(msg, type === 'reject' ? 3000 : undefined, type),
    setRowLoading,
    setRowError,
    setRowSuccess,
    setRowAction,
    setLoading
  })

  // Fetch stats function (extracted so we can call it for refresh)
  const fetchStats = async () => {
    if (!projectId || !isAuthenticated) return

    // Store reference for fuel log operations hook
    fetchStatsRef.current = fetchStats

    try {
      // Handle All Projects view first (before cost management check)
      if (isCostModuleView(projectId)) {
        // PROGRESSIVE RENDERING: Data processing now handled by useEffect watching allProjectsForms
        // This just initializes the loading state
        console.log('[ApprovalSummary] Cost Module view - progressive rendering via useEffect')
        setLoading(true)
        setPendingReports([])
        setStats({ total: 0, pendingApproval: 0, completed: 0, open: 0 })
        setSelectedMap({})

        // NOTE: Actual data processing happens in the useEffect that watches allProjectsForms
        // Forms will appear progressively as projects load in ProjectContext
      } else if (projectId === PROJECT_IDS.HOCON_SANDBOX) {
        setLoading(true)
        // Hocon Sandbox - fetch ACC Forms using Forms V2 API (only inReview)
        // Note: Forms V2 API has a maximum limit of 50
        const inReviewForms = await getAllProjectForms(projectId, 'inReview', 50)

        setStats({ total: inReviewForms.length, pendingApproval: inReviewForms.length, completed: 0, open: 0 })

        // Transform forms to match the expected report structure
        const transformedForms = inReviewForms.map(form => ({
          ...form,
          id: form.id || form.formId,
          title: form.name || form.title || form.formName,
          status: form.status,
          _isForm: true // Flag to identify this as a form, not a field report
        }))

        setPendingReports(transformedForms)
        setSelectedMap({})
      } else if (isCostManagementProject(projectId)) {
        // Cost Management projects - fetch contracts and subcontracts
        // Use cost workflows hook - state sync (including loading state) handled by useEffect
        // Loading state is automatically managed by the hook and synced via useEffect
        await costWorkflowsHook.fetchWorkflows()

        // Reset selection when data refreshes
        setSelectedMap({})
      } else if (templateId) {
        setLoading(true)

        // Equipment Yard: fetch from multiple template IDs (only inReview status)
        const templateIds = config?.fuelLogTemplateIds || [templateId]

        // Fetch form reports first - we'll extract template info from them
        // Only fetch inReview status - skip closed and inProgress to reduce API calls
        const requests = templateIds.map(tid =>
          () => getFieldReports(projectId, 50, 0, tid, 'inReview')
        )
        const responses = await autodeskApiThrottler.throttleAll(requests)

        // Extract template structure from ALL forms (not just first)
        const fieldDefs = {}
        const allReports = []

        responses.forEach((response, index) => {
          const tid = templateIds[index]
          const reports = response?.reports || []

          // Always check if we have a predefined template configuration
          // This ensures we can edit forms even if there are currently no forms in review
          const templateConfig = TEMPLATE_FIELD_CONFIGS[tid]

          if (templateConfig && templateConfig.fields) {
            // Use predefined configuration - works even if no forms exist
            let templateName = templateConfig.name
            let fieldIdSet = new Set(templateConfig.fields)
            const fieldValueTypes = {}

            // Mark explicitly defined toggle fields
            const toggleFieldIds = new Set(templateConfig.toggleFields || [])
            toggleFieldIds.forEach(fid => {
              fieldValueTypes[fid] = 'toggle_val'
            })

            // If we have reports, determine value types from actual form data
            if (reports.length > 0) {
              reports.forEach(report => {
                const customValues = report.nativeForm?.customValues || []
                customValues.forEach(cv => {
                  const fid = cv.fieldId || cv.id
                  if (fid && !cv.deleted && fieldIdSet.has(fid)) {
                    // Only determine type for fields in our predefined list (skip if already set as toggle)
                    if (!fieldValueTypes[fid]) {
                      if (cv.numberVal !== undefined) fieldValueTypes[fid] = 'number_val'
                      else if (cv.dateVal !== undefined) fieldValueTypes[fid] = 'date_val'
                      else if (cv.boolVal !== undefined) fieldValueTypes[fid] = 'bool_val'
                      else if (cv.choiceVal !== undefined) fieldValueTypes[fid] = 'choice_val'
                      else if (cv.toggleVal !== undefined) fieldValueTypes[fid] = 'toggle_val'
                      else fieldValueTypes[fid] = 'text_val'
                    }
                  }
                })
              })
            } else {
              // No reports to analyze - use default text_val for undefined types
              fieldIdSet.forEach(fid => {
                if (!fieldValueTypes[fid]) {
                  fieldValueTypes[fid] = 'text_val'
                }
              })
            }

            // Build field definitions
            const fields = Array.from(fieldIdSet).map(fid => ({
              fieldId: fid,
              label: DEFAULT_FIELD_NAME_MAP[fid] || fid,
              valueType: fieldValueTypes[fid] || 'text_val',
              sectionLabel: ''
            }))


            fieldDefs[tid] = {
              fields: fields,
              fieldMap: createFieldNameMap(fields),
              templateName: templateName
            }

            // Add template metadata to each report
            reports.forEach(report => {
              allReports.push({
                ...report,
                _templateId: tid,
                _templateName: templateName
              })
            })
          } else {
            // Fallback: No predefined config, extract fields dynamically from forms
            if (reports.length > 0) {
              let templateName = reports[0].name || `Template ${index + 1}`
              let fieldIdSet = new Set()
              const fieldValueTypes = {}

              // Collect field IDs from ALL forms
              reports.forEach(report => {
                const customValues = report.nativeForm?.customValues || []
                customValues.forEach(cv => {
                  const fid = cv.fieldId || cv.id
                  if (fid && !cv.deleted) {
                    fieldIdSet.add(fid)

                    // Determine value type from the first occurrence
                    if (!fieldValueTypes[fid]) {
                      if (cv.numberVal !== undefined) fieldValueTypes[fid] = 'number_val'
                      else if (cv.dateVal !== undefined) fieldValueTypes[fid] = 'date_val'
                      else if (cv.boolVal !== undefined) fieldValueTypes[fid] = 'bool_val'
                      else if (cv.choiceVal !== undefined) fieldValueTypes[fid] = 'choice_val'
                      else if (cv.toggleVal !== undefined) fieldValueTypes[fid] = 'toggle_val'
                      else fieldValueTypes[fid] = 'text_val'
                    }
                  }
                })
              })

              // Build field definitions
              const fields = Array.from(fieldIdSet).map(fid => ({
                fieldId: fid,
                label: DEFAULT_FIELD_NAME_MAP[fid] || fid,
                valueType: fieldValueTypes[fid] || 'text_val',
                sectionLabel: ''
              }))

              fieldDefs[tid] = {
                fields: fields,
                fieldMap: createFieldNameMap(fields),
                templateName: templateName
              }

              // Add template metadata to each report
              reports.forEach(report => {
                allReports.push({
                  ...report,
                  _templateId: tid,
                  _templateName: templateName
                })
              })
            }
          }
        })

        setTemplateFieldDefs(fieldDefs)

        setStats({ total: allReports.length, pendingApproval: allReports.length, completed: 0, open: 0 })
        setPendingReports(allReports)

        // reset selection when list changes
        setSelectedMap({})
      }
    } catch (error) {
      console.error('[ApprovalSummary] Error fetching stats:', error)
      // Error fetching stats - silently handle
    } finally {
      // For All Projects, keep loading true if context data is not available yet
      if (isCostModuleView(projectId)) {
        const hasContextData = Object.keys(allProjectsForms).length > 0
        if (hasContextData) {
          setLoading(false)
        }
        // If no context data yet, keep loading true
      } else {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    // Clear previous project data when projectId changes
    setPendingReports([])
    setSelectedMap({})
    setEditingRow(null)
    setContractAttachments({})
    setContractPackages({})
    setContractWorkflowHistory({})
    setContractPendingApprovals({})
    setStats({ total: 0, pendingApproval: 0, completed: 0, open: 0 })
    setSearchQuery('') // Clear search query when switching projects
    setTemplateFieldDefs({}) // Clear template field definitions when switching projects

    // Set loading state immediately for Cost Module to show skeleton loaders
    if (isCostModuleView(projectId)) {
      setLoading(true)
    } else {
      // For non-Cost Module views, set loading=true initially
      // It will be set to false when data loads or if there's an error
      setLoading(true)
    }

    // Fetch new data
    fetchStats()
  }, [projectId, templateId, isAuthenticated])

  // PROGRESSIVE RENDERING: React to incremental allProjectsForms updates
  // This processes and displays forms as each project batch loads from ProjectContext
  useEffect(() => {
    if (!isCostModuleView(projectId)) return
    if (Object.keys(allProjectsForms).length === 0) return

    // Process forms from allProjectsForms progressively
    const processFormsProgressively = async () => {
      console.log('[ApprovalSummary] Processing forms progressively from context...')
      const progressiveStart = window.performance.now()

      // Aggregate forms from all loaded projects
      const inReviewForms = []
      Object.keys(allProjectsForms).forEach(projId => {
        const projectData = allProjectsForms[projId]
        if (projectData) {
          const project = projects.find(p => p.id === projId)
          const projectName = project?.name || 'Unknown Project'

          const formsWithProjectName = (projectData.inReview || []).map(form => ({
            ...form,
            _projectName: projectName,
            _sourceProjectId: projId
          }))

          inReviewForms.push(...formsWithProjectName)
        }
      })

      if (inReviewForms.length === 0) {
        // No forms yet, but projects are loading - keep skeleton
        console.log('[ApprovalSummary] No forms yet from loaded projects')
        return
      }

      // Filter by user assignment in small batches for fast initial display
      const BATCH_SIZE = 5  // Smaller batches for faster initial render
      const filteredForms = []

      const totalBatches = Math.ceil(inReviewForms.length / BATCH_SIZE)

      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const startIdx = batchIndex * BATCH_SIZE
        const batch = inReviewForms.slice(startIdx, startIdx + BATCH_SIZE)

        // Fetch pending approvals for this batch
        const pendingApprovalsData = {}
        await Promise.all(batch.map(async (form) => {
          try {
            const sourceProjectId = form._sourceProjectId
            const formId = form.id || form.formId || form.reportId

            if (!sourceProjectId || !formId) return

            const isChangeOrder = form._isChangeOrder || form._changeOrderType

            if (isChangeOrder) {
              const activeTasks = form.workflow?.costStatus?.currentStep?.activeTasks ||
                                  form.workflow?.budgetStatus?.currentStep?.activeTasks ||
                                  []
              pendingApprovalsData[formId] = activeTasks
            } else {
              const pendingRes = await axios.get(
                `${API_BASE_URL}/api/cost/${sourceProjectId}/contracts/${formId}/pending-approvals`,
                {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  withCredentials: true
                }
              ).catch(() => ({ data: { success: false, pendingTasks: [] } }))

              pendingApprovalsData[formId] = pendingRes.data.success ? (pendingRes.data.pendingTasks || []) : []
            }
          } catch (error) {
            console.error('[ApprovalSummary] Error fetching pending approvals:', error)
          }
        }))

        // Filter this batch by user
        const currentUserEmail = user?.email?.toLowerCase()
        const currentUserId = user?.oxygenId || user?.id || user?.autodeskId || user?.userId
        const currentUsername = user?.name?.toLowerCase() || user?.username?.toLowerCase()
        const currentUserRoleIds = user?.roleIds || []

        const batchFiltered = batch.filter(form => {
          const formId = form.id || form.formId || form.reportId
          const pendingTasks = pendingApprovalsData[formId] || []

          if (pendingTasks.length === 0) return false

          return pendingTasks.some(task => {
            if (task._assignmentType === 'role') {
              if (task.roleId && currentUserRoleIds.length > 0) {
                return currentUserRoleIds.includes(task.roleId)
              }
              return false
            }

            if (task._assignmentType === 'user') {
              const assigneeId = task.oxygenId
              const assigneeEmail = task.email?.toLowerCase()
              const assigneeName = task.name?.toLowerCase()

              const idMatch = assigneeId && currentUserId && (
                assigneeId === currentUserId ||
                assigneeId.toString() === currentUserId.toString()
              )
              const emailMatch = assigneeEmail && currentUserEmail && assigneeEmail === currentUserEmail
              const nameMatch = assigneeName && currentUsername && assigneeName === currentUsername

              return idMatch || emailMatch || nameMatch
            }

            const assigneeEmail = (task.assignedTo?.email || task.assignee?.email || task.email || '').toLowerCase()
            const emailMatch = assigneeEmail && currentUserEmail && assigneeEmail === currentUserEmail

            const assigneeId = task.assignee?.oxygenId || task.oxygenId || task.assignee?.id
            const idMatch = assigneeId && currentUserId && (
              assigneeId === currentUserId ||
              assigneeId.toString() === currentUserId.toString()
            )

            return emailMatch || idMatch
          })
        })

        filteredForms.push(...batchFiltered)

        // Show results immediately - progressive update!
        const transformedForms = filteredForms.map(form => ({
          ...form,
          id: form.id || form.formId || form.reportId,
          title: form.name || form.title || form.formName,
          status: form.status,
          projectName: form._projectName,
          _isAllProjects: true,
          _metadataLoaded: false
        }))

        setPendingReports(transformedForms)
        setStats({
          total: transformedForms.length,
          pendingApproval: transformedForms.length,
          completed: 0,
          open: 0
        })

        // Hide skeleton loaders after first batch
        if (batchIndex === 0 && filteredForms.length > 0) {
          setLoading(false)
          console.log(`[ApprovalSummary] ✓ First forms visible! (${(window.performance.now() - progressiveStart).toFixed(0)}ms)`)
        }

        // Log progress every few batches
        if (batchIndex === 0 || (batchIndex + 1) % 5 === 0 || batchIndex === totalBatches - 1) {
          console.log(`[ApprovalSummary] Batch ${batchIndex + 1}/${totalBatches}: ${transformedForms.length} forms displayed`)
        }
      }

      // All batches processed
      if (filteredForms.length === 0) {
        setLoading(false)
      }
      console.log(`[ApprovalSummary] ✓ Progressive processing complete: ${filteredForms.length} user-assigned forms`)

      // BACKGROUND METADATA LOADING: Now fetch metadata for all forms
      if (filteredForms.length > 0) {
        console.log('[ApprovalSummary] Starting background metadata loading...')
        const metadataStart = window.performance.now()

        // Fetch metadata for all forms in parallel (with reasonable concurrency)
        const METADATA_BATCH_SIZE = 10
        for (let i = 0; i < filteredForms.length; i += METADATA_BATCH_SIZE) {
          const metadataBatch = filteredForms.slice(i, i + METADATA_BATCH_SIZE)

          await Promise.all(metadataBatch.map(async (form) => {
            const sourceProjectId = form._sourceProjectId
            const formId = form.id || form.formId || form.reportId

            if (!sourceProjectId || !formId) return

            try {
              const isChangeOrder = form._isChangeOrder || form._changeOrderType

              // Fetch all metadata in parallel (including pending approvals)
              const [attachmentRes, packagesRes, historyRes, pendingApprovalsRes] = await Promise.all([
                // Attachments
                axios.get(
                  isChangeOrder
                    ? `${API_BASE_URL}/api/cost/${sourceProjectId}/change-orders/${formId}/attachments`
                    : `${API_BASE_URL}/api/cost/${sourceProjectId}/contracts/${formId}/attachments`,
                  {
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    withCredentials: true
                  }
                ).catch(() => ({ data: { success: false, attachments: [] } })),

                // Packages
                axios.get(
                  isChangeOrder
                    ? `${API_BASE_URL}/api/cost/${sourceProjectId}/change-orders/${formId}/packages`
                    : `${API_BASE_URL}/api/cost/${sourceProjectId}/contracts/${formId}/packages`,
                  {
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    withCredentials: true
                  }
                ).catch(() => ({ data: { success: false, packages: [] } })),

                // Workflow history (only for contracts)
                !isChangeOrder ? axios.get(
                  `${API_BASE_URL}/api/cost/${sourceProjectId}/contracts/${formId}/workflow-history`,
                  {
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    withCredentials: true
                  }
                ).catch(() => ({ data: { success: false, history: [] } })) : Promise.resolve({ data: { success: false, history: [] } }),

                // Pending approvals (only for contracts)
                !isChangeOrder ? axios.get(
                  `${API_BASE_URL}/api/cost/${sourceProjectId}/contracts/${formId}/pending-approvals`,
                  {
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    withCredentials: true
                  }
                ).catch(() => ({ data: { success: false, pendingTasks: [] } })) : Promise.resolve({ data: { success: false, pendingTasks: [] } })
              ])

              // Update state with fetched metadata
              setContractAttachments(prev => ({
                ...prev,
                [formId]: attachmentRes.data.success ? (attachmentRes.data.attachments || []) : []
              }))

              setContractPackages(prev => ({
                ...prev,
                [formId]: packagesRes.data.success ? (packagesRes.data.packages || []) : []
              }))

              setContractWorkflowHistory(prev => ({
                ...prev,
                [formId]: historyRes.data.success ? (historyRes.data.history || []) : []
              }))

              setContractPendingApprovals(prev => ({
                ...prev,
                [formId]: pendingApprovalsRes.data.success ? (pendingApprovalsRes.data.pendingTasks || []) : []
              }))
            } catch (error) {
              console.error(`[ApprovalSummary] Error loading metadata for form ${formId}:`, error)
            }
          }))
        }

        console.log(`[ApprovalSummary] ✓ Metadata loading complete (${(window.performance.now() - metadataStart).toFixed(0)}ms)`)
      }
    }

    processFormsProgressively()
  }, [allProjectsForms, projectId, user, token, projects])

  // Fetch permissions for all templates when templateFieldDefs change
  useEffect(() => {
    const fetchTemplatePermissions = async () => {
      if (!projectId || !isAuthenticated) return
      if (Object.keys(templateFieldDefs).length === 0) return

      // Skip for All Projects and Cost Management
      if (isCostModuleView(projectId) || isCostManagementProject(projectId)) return

      try {
        const templateIds = Object.keys(templateFieldDefs)

        // Fetch permissions for all templates in parallel
        const permissionsPromises = templateIds.map(async (tid) => {
          try {
            const permissions = await checkFormAccess(projectId, tid)
            return { templateId: tid, permissions }
          } catch (error) {
            console.error(`[ApprovalSummary] Error fetching permissions for template ${tid}:`, error)
            // Return view-only permissions on error
            return {
              templateId: tid,
              permissions: {
                hasReviewerAccess: false,
                accessLevels: {
                  canView: true,
                  canEdit: false,
                  canApprove: false,
                  canReject: false
                }
              }
            }
          }
        })

        const results = await Promise.all(permissionsPromises)

        // Build permissions map
        const permissionsMap = {}
        results.forEach(({ templateId, permissions }) => {
          permissionsMap[templateId] = {
            canView: permissions?.accessLevels?.canView ?? true,
            canEdit: permissions?.accessLevels?.canEdit ?? false,
            canApprove: permissions?.accessLevels?.canApprove ?? false,
            canReject: permissions?.accessLevels?.canReject ?? false,
            hasReviewerAccess: permissions?.hasReviewerAccess ?? false
          }
        })

        setTemplatePermissions(permissionsMap)
      } catch (error) {
        console.error('[ApprovalSummary] Error fetching template permissions:', error)
      }
    }

    fetchTemplatePermissions()
  }, [projectId, templateFieldDefs, isAuthenticated])


  // Fetch locations and build locationMap
  useEffect(() => {
    const fetchLocations = async () => {
      if (!projectId || !isAuthenticated) return

      try {
        // For All Projects, fetch locations from all real projects and merge them
        if (isCostModuleView(projectId)) {
          setLoadingLocations(true)
          const allLocationsMap = {}

          // Filter out invalid project IDs and fetch locations from each real project
          const validProjects = projects.filter(p => p.id && !isCostModuleView(p.id))

          await Promise.all(validProjects.map(async (project) => {
            try {
              const response = await getProjectLocations(project.id)
              const locations = response.locations || []

              // Add each location to the merged map
              locations.forEach(loc => {
                if (loc.id && loc.name) {
                  allLocationsMap[loc.id] = loc.name
                }
              })

            } catch (error) {
              console.error(`Failed to fetch locations for project ${project.id}:`, error)
              // Continue with other projects even if one fails
            }
          }))

          setLocationMap(allLocationsMap)
          setLocationsList([])
          setLoadingLocations(false)
          return
        }

        // For individual projects, fetch locations normally
        setLoadingLocations(true)
        const response = await getProjectLocations(projectId)
        const locations = response.locations || []

        // Sort locations by order key
        const sortedLocs = sortLocations(locations)

        // Build a map of locationId -> locationName
        const map = buildLocationMap(sortedLocs)

        setLocationMap(map)
        setLocationsList(sortedLocs)
        setLoadingLocations(false)
      } catch (error) {
        console.error('Failed to fetch locations:', error)
        // Set empty data to allow component to continue rendering
        setLocationMap({})
        setLocationsList([])
        setLoadingLocations(false)
      }
    }

    fetchLocations()
  }, [projectId, isAuthenticated, projects])

  // Fetch assets and build formAssetMap (formId -> clientAssetId) via Relationships API
  // Only runs when pendingReports are loaded so we can pass form IDs
  useEffect(() => {
    const fetchAssets = async () => {
      if (!projectId || !isAuthenticated) return
      if (isCostModuleView(projectId)) return
      if (isCostManagementProject(projectId)) return
      if (pendingReports.length === 0) return

      try {
        const formIds = pendingReports.map(r => r.id).filter(Boolean)
        const data = await getProjectAssets(projectId, formIds)
        setAssetMap(data.assetMap || {})
        setFormAssetMap(data.formAssetMap || {})
      } catch (error) {
        console.warn('[ApprovalSummary] Could not fetch assets for matching:', error.message)
      }
    }

    fetchAssets()
  }, [projectId, isAuthenticated, pendingReports])

  if (!isAuthenticated) return null

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'))
    else { setSortKey(key); setSortDir('asc') }
  }

  const sortedReports = useMemo(() => {
    return sortData(pendingReports, sortKey, sortDir, ['createdAt', 'formDate'])
  }, [pendingReports, sortKey, sortDir])

  // Filter reports based on search query (searches Asset and Location)
  const filteredReports = useMemo(() => {
    if (!searchQuery.trim()) return sortedReports

    const query = searchQuery.toLowerCase().trim()

    // All known Equipment Number field IDs from different templates
    const equipmentNumberFieldIds = [
      '6421984c-9b70-425e-837c-556d6483cde2', // Service Station Fuel Log
      'd9879716-f7ff-4264-a58c-3b6e45b5ee72', // Highway Diesel
      'ab82979f-a5d2-461c-8292-7d753dd6a7a1', // Gasoline
      'd2ac6769-6f5b-4408-872d-54c2e5911752'  // Off Highway Diesel
    ]

    return sortedReports.filter(report => {
      // Get location name
      const locationName = (locationMap[report.locationId] || '').toLowerCase()

      // Collect all searchable strings for this report
      const searchableStrings = []

      // 1. Add location name
      if (locationName) {
        searchableStrings.push(locationName)
      }

      // 2. Check formAssetMap for direct form->asset relationship (clientAssetId)
      const clientAssetId = formAssetMap[report.id]
      if (clientAssetId) {
        searchableStrings.push(clientAssetId.toLowerCase())
      }

      // 3. Check nativeForm.customValues for Equipment Number field value
      if (report.nativeForm?.customValues) {
        const equipmentField = report.nativeForm.customValues.find(field =>
          equipmentNumberFieldIds.includes(field.fieldId) ||
          field.label?.toLowerCase().includes('equipment number')
        )
        if (equipmentField) {
          const equipmentValue = equipmentField.textVal || equipmentField.choiceVal || ''
          if (equipmentValue) {
            searchableStrings.push(equipmentValue.toLowerCase())

            // Also check if this equipment number maps to a clientAssetId in assetMap
            if (assetMap[equipmentValue]) {
              searchableStrings.push(assetMap[equipmentValue].toLowerCase())
            }
          }
        }
      }

      // 4. Check report.name for asset info
      if (report.name) {
        searchableStrings.push(report.name.toLowerCase())
      }

      // Match query against any of the searchable strings
      return searchableStrings.some(str => str.includes(query))
    })
  }, [sortedReports, searchQuery, locationMap, assetMap, formAssetMap])

  // Compute allSelected based on filtered reports (must come after filteredReports)
  const allSelected = useMemo(() => {
    if (filteredReports.length === 0) return false
    return filteredReports.every(r => selectedMap[r.id])
  }, [filteredReports, selectedMap])

  // Reset selection when search query changes
  useEffect(() => {
    setSelectedMap({})
  }, [searchQuery])

  // Toggle single row selection
  const toggleSelect = (reportId) => {
    setSelectedMap(prev => toggleItemSelection(prev, reportId))
  }

  // Select all / Deselect all (only filtered reports)
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedMap(deselectAllItems())
    } else {
      setSelectedMap(selectAllItems(filteredReports))
    }
  }

  // Select all / Deselect all for a specific set of reports (used by TemplateTable)
  const toggleSelectAllForReports = (reports) => {
    // Check if all reports in this set are selected
    const allReportsSelected = reports.every(r => selectedMap[r.id])

    if (allReportsSelected) {
      // Deselect only these reports
      setSelectedMap(prev => {
        const newMap = { ...prev }
        reports.forEach(r => {
          delete newMap[r.id]
        })
        return newMap
      })
    } else {
      // Select all these reports
      setSelectedMap(prev => {
        const newMap = { ...prev }
        reports.forEach(r => {
          newMap[r.id] = true
        })
        return newMap
      })
    }
  }

  // Modal helpers
  // per-row approve handler
  const handleApproveRow = async (report) => {
    const isContract = report._isContract || report._isSubcontract
    const formName = report.formName || report.name || report.code || 'this form'

    const confirmed = await confirmModal.confirm(
      `Are you sure you want to approve ${isContract ? 'workflow' : 'form'} "${formName}"?`,
      'Yes, Approve',
      'Cancel'
    )
    if (!confirmed) return

    // Check if this is a contract or subcontract
    if (isContract) {
      return contractOps.approveContract(report)
    }

    // Use fuel log operations hook for field reports
    return fuelLogOps.approveFuelLog(report)
  }

  // Contract handlers are now provided by useContractOperations hook

  // per-row reject handler
  const handleRejectRow = async (report) => {
    const isContract = report._isContract || report._isSubcontract
    const formName = report.formName || report.name || report.code || 'this form'

    const confirmed = await confirmModal.confirm(
      `Are you sure you want to reject ${isContract ? 'workflow' : 'form'} "${formName}"?`,
      'Yes, Reject',
      'Cancel'
    )
    if (!confirmed) return

    // Check if this is a contract or subcontract
    if (isContract) {
      return contractOps.rejectContract(report)
    }

    // Use fuel log operations hook for field reports
    return fuelLogOps.rejectFuelLog(report)
  }

  // Bulk approve selected forms
  const bulkApprove = async () => {
    const selectedIds = Object.keys(selectedMap).filter(id => selectedMap[id])
    if (selectedIds.length === 0) {
      successModal.open('No forms selected for approval', 3000, 'reject')
      return
    }

    // Check if we're dealing with contracts/subcontracts
    const selectedReports = pendingReports.filter(r => selectedIds.includes(r.id))
    const areContracts = selectedReports.length > 0 && (selectedReports[0]._isContract || selectedReports[0]._isSubcontract)

    const confirmed = await confirmModal.confirm(
      `Are you sure you want to approve ${selectedIds.length} selected ${areContracts ? 'workflow(s)' : 'form(s)'}?`,
      'Yes, Approve',
      'Cancel'
    )
    if (!confirmed) return

    if (areContracts) {
      // Use contract operations hook for contracts
      await contractOps.bulkApproveContracts(selectedIds)
    } else {
      // Use fuel log operations hook for field reports
      // Pass full report objects instead of just IDs to support multiple templates
      await fuelLogOps.bulkApproveFuelLogs(selectedReports)
    }
  }

  // Enter edit mode for a row
  const handleEditRow = (report) => {
    setEditingRow(report.id)

    // Check if this is a template-specific form (has _templateId or formTemplateId)
    const isTemplateForm = !!(report._templateId || report.formTemplateId)

    if (isTemplateForm) {
      // Get template config to check for toggle fields
      const templateId = report._templateId || report.formTemplateId || report.templateId
      const templateConfig = TEMPLATE_FIELD_CONFIGS[templateId]
      const toggleFieldIds = new Set(templateConfig?.toggleFields || [])

      // Initialize template-specific fields with current values
      const initialValues = {}

      report.nativeForm.customValues.forEach(cv => {
        const fieldId = cv.fieldId || cv.id
        if (!cv.deleted && fieldId) {
          // Check if this is a toggle field
          if (toggleFieldIds.has(fieldId)) {
            // Toggle field: convert various formats to numeric (true=1, false=0)
            let rawValue = cv.toggleVal !== undefined ? cv.toggleVal : cv.boolVal
            if (rawValue !== undefined) {
              // Convert string "True"/"False" or boolean true/false to numeric
              if (rawValue === true || rawValue === 'true' || rawValue === 'True' || rawValue === 1 || rawValue === '1') {
                initialValues[fieldId] = 1
              } else if (rawValue === false || rawValue === 'false' || rawValue === 'False' || rawValue === 0 || rawValue === '0') {
                initialValues[fieldId] = 0
              } else if (rawValue === 2 || rawValue === '2' || rawValue === 'NA' || rawValue === 'na') {
                initialValues[fieldId] = 2
              } else {
                initialValues[fieldId] = rawValue
              }
            }
          }
          // Determine value based on type for non-toggle fields
          else if (cv.numberVal !== undefined) {
            initialValues[fieldId] = cv.numberVal
          } else if (cv.choiceVal !== undefined) {
            initialValues[fieldId] = cv.choiceVal
          } else if (cv.textVal !== undefined) {
            initialValues[fieldId] = cv.textVal
          } else if (cv.dateVal !== undefined) {
            initialValues[fieldId] = cv.dateVal
          } else if (cv.boolVal !== undefined) {
            initialValues[fieldId] = cv.boolVal
          } else if (cv.toggleVal !== undefined) {
            initialValues[fieldId] = cv.toggleVal
          }
        }
      })

      // Also include current location
      initialValues.locationId = report.locationId || ''

      // Store field IDs for use during save
      // Try to get from customValues first, but fallback to template definition if empty
      let fieldIds = []
      if (report.nativeForm.customValues && report.nativeForm.customValues.length > 0) {
        fieldIds = report.nativeForm.customValues
          .filter(cv => !cv.deleted && (cv.fieldId || cv.id))
          .map(cv => cv.fieldId || cv.id)
      } else {
        // Fallback: get from template definition
        const templateDef = templateFieldDefs[templateId]
        if (templateDef && templateDef.fields) {
          fieldIds = templateDef.fields.map(f => f.fieldId)
        }
      }
      setEditingFieldIds(fieldIds)

      // Use nested structure for template forms: editedValues[reportId][fieldId]
      setEditedValues({ [report.id]: initialValues })
    } else {
      // Original logic for non-template forms
      const initialValues = {}
      FIELD_IDS.forEach(fieldId => {
        initialValues[fieldId] = getFieldValue(report, fieldId)
      })
      // Also include current location
      initialValues.locationId = report.locationId || ''

      // Store original location and job number for validation logic
      initialValues._originalLocationId = report.locationId || ''
      initialValues._originalJobNumber = getFieldValue(report, 'e2f5badc-d154-4c71-8ae2-1d8c678e3d7d')

      setEditedValues(initialValues)
    }
  }

  // Cancel edit mode
  const handleCancelEdit = () => {
    setEditingRow(null)
    setEditedValues({})
    setEditingFieldIds([])
    setLocationDropdownOpen(false)
  }

  // Update a field value in edit mode
  // Supports two signatures:
  // 1. handleFieldChange(fieldId, value) - for non-template forms
  // 2. handleFieldChange(reportId, fieldId, value) - for template forms
  const handleFieldChange = (arg1, arg2, arg3) => {
    const jobNumberFieldId = 'e2f5badc-d154-4c71-8ae2-1d8c678e3d7d'

    // Detect signature: if arg3 exists, it's (reportId, fieldId, value)
    const isTemplateForm = arg3 !== undefined

    if (isTemplateForm) {
      // Template form signature: (reportId, fieldId, value)
      const reportId = arg1
      const fieldId = arg2
      const value = arg3

      setEditedValues(prev => {
        // For template forms, ONLY keep the nested structure for this report
        // Do NOT spread prev to avoid keeping stale field IDs from previous edit sessions
        return {
          [reportId]: {
            ...(prev[reportId] || {}),
            [fieldId]: value
          }
        }
      })
    } else {
      // Non-template form signature: (fieldId, value)
      const fieldId = arg1
      const value = arg2

      // Special handling for location field changes
      if (fieldId === 'locationId') {
        // When location is edited/selected, always update Job# with the location name
        if (value) {
          const locationName = locationMap[value] || value
          setEditedValues(prev => ({
            ...prev,
            locationId: value,
            [jobNumberFieldId]: locationName
          }))
          return
        } else {
          // If location is cleared, just update locationId
          setEditedValues(prev => ({
            ...prev,
            locationId: value
          }))
          return
        }
      }

      // If user is editing Job# field and location is set, block the job# change
      if (fieldId === jobNumberFieldId) {
        const currentLocationId = editedValues.locationId || ''
        if (currentLocationId) {
          // Location is set, don't allow job# changes
          return
        }
      }

      setEditedValues(prev => ({
        ...prev,
        [fieldId]: value
      }))
    }
  }

  // Save edited values
  const handleSaveEdit = async (report) => {
    // Check if this is a template-specific form
    // Use _templateId instead of customValues because customValues might be empty after save
    const isTemplateForm = !!(report._templateId || report.formTemplateId)

    if (isTemplateForm) {
      // For template forms, get the edited values for this specific report
      const reportEditedValues = editedValues[report.id] || {}

      // Use the field IDs that were stored when edit mode started
      const fieldIds = editingFieldIds

      // Use fuel log operations hook to save changes
      const success = await fuelLogOps.saveFuelLogEdit(report, reportEditedValues, fieldIds)

      // If save was successful, exit edit mode
      if (success) {
        setEditingRow(null)
        setEditedValues({})
        setEditingFieldIds([])
        setLocationDropdownOpen(false)
      }
    } else {
      // Original logic for non-template forms
      const success = await fuelLogOps.saveFuelLogEdit(report, editedValues, FIELD_IDS)

      // If save was successful, exit edit mode
      if (success) {
        setEditingRow(null)
        setEditedValues({})
        setEditingFieldIds([])
        setLocationDropdownOpen(false)
      }
    }
  }

  // Column resize handlers
  const handleResizeStart = (e, columnKey) => {
    e.preventDefault()
    e.stopPropagation()

    // Get the actual column header element to measure its current width
    const thElement = e.target.closest('th')
    const currentWidth = thElement ? thElement.offsetWidth : 150

    setResizingColumn(columnKey)
    setStartX(e.clientX)
    setStartWidth(currentWidth)
  }

  // Add event listeners for resize
  useEffect(() => {
    if (!resizingColumn) return

    const handleResizeMove = (e) => {
      const diff = e.clientX - startX
      const newWidth = Math.max(50, startWidth + diff) // Minimum 50px

      setColumnWidths(prev => ({
        ...prev,
        [resizingColumn]: newWidth
      }))
    }

    const handleResizeEnd = () => {
      setResizingColumn(null)
    }

    document.addEventListener('mousemove', handleResizeMove)
    document.addEventListener('mouseup', handleResizeEnd)

    return () => {
      document.removeEventListener('mousemove', handleResizeMove)
      document.removeEventListener('mouseup', handleResizeEnd)
    }
  }, [resizingColumn, startX, startWidth])

  // Sticky header scroll listener
  useEffect(() => {
    const handleScroll = () => {
      if (theadRef.current) {
        const theadRect = theadRef.current.getBoundingClientRect()
        const headerHeight = 60 // Fixed page header height

        // Table header should become sticky when it reaches just below the page header
        // We check if the thead's top is at or above the header height
        const shouldBeSticky = theadRect.top <= headerHeight

        setIsHeaderSticky(shouldBeSticky)
      }
    }

    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleScroll)
    // Check initial state
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [pendingReports.length])

  // Bulk reject selected forms
  const bulkReject = async () => {
    const selectedIds = Object.keys(selectedMap).filter(id => selectedMap[id])
    if (selectedIds.length === 0) {
      successModal.open('No forms selected for rejection', 3000, 'reject')
      return
    }

    // Check if we're dealing with contracts/subcontracts
    const selectedReports = pendingReports.filter(r => selectedIds.includes(r.id))
    const areContracts = selectedReports.length > 0 && (selectedReports[0]._isContract || selectedReports[0]._isSubcontract)

    const confirmed = await confirmModal.confirm(
      `Are you sure you want to reject ${selectedIds.length} selected ${areContracts ? 'workflow(s)' : 'form(s)'}?`,
      'Yes, Reject',
      'Cancel'
    )
    if (!confirmed) return

    if (areContracts) {
      // Use contract operations hook for contracts/subcontracts
      await contractOps.bulkRejectContracts(selectedIds)
    } else {
      // Use fuel log operations hook for field reports
      // Pass full report objects instead of just IDs to support multiple templates
      await fuelLogOps.bulkRejectFuelLogs(selectedReports)
    }
  }

  // Attachment handlers are now provided by useAttachmentOperations hook

  // Determine if this is a Cost Management project for subcontracts
  // Exclude "Cost Module" view from Cost Management
  const isCostManagementProj = !isCostModuleView(projectId) && isCostManagementProject(projectId)

  // Calculate if any checkboxes are selected for bulk operations
  const isAnyCheckboxSelected = useMemo(() => {
    return Object.values(selectedMap).some(selected => selected)
  }, [selectedMap])

  // Calculate the count of selected forms
  const selectedCount = useMemo(() => {
    return Object.values(selectedMap).filter(selected => selected).length
  }, [selectedMap])

  // Check if user has reviewer access OR manage access for any template
  const hasAnyReviewerAccess = useMemo(() => {
    return Object.values(templatePermissions).some(perm =>
      perm.hasReviewerAccess === true || perm.canEdit === true
    )
  }, [templatePermissions])

  return (
    <div className="approval-summary-container">
      <div className="approval-summary-card">
        {/* Desktop Layout */}
        <div className="approval-summary-header approval-summary-header-desktop">
          <h3 className="approval-summary-title">Forms Awaiting Approval</h3>

          {/* Search Bar - Only for Equipment Yard */}
          {projectId === PROJECT_IDS.EQUIPMENT_YARD && (
            <div className="approval-summary-search">
              <input
                type="text"
                className="approval-summary-search-input"
                placeholder="Search by Asset or Location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="approval-summary-search-clear"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          )}

          <div className="approval-summary-stats">
            <div>In Review: <strong>{stats.pendingApproval}</strong></div>
            {searchQuery && projectId === PROJECT_IDS.EQUIPMENT_YARD && (
              <div>Filtered: <strong>{filteredReports.length}</strong></div>
            )}
          </div>

          <div className="approval-summary-toolbar">
            {!isCostManagementProject(projectId) && !isCostModuleView(projectId) && hasAnyReviewerAccess && (
              <>
                <Button
                  variant="blue"
                  icon="✓"
                  onClick={bulkApprove}
                  disabled={Object.keys(selectedMap).length === 0}
                >
                  Bulk Approve ({Object.keys(selectedMap).length})
                </Button>

                <Button
                  variant="red"
                  icon="✕"
                  onClick={bulkReject}
                  disabled={Object.keys(selectedMap).length === 0}
                >
                  Bulk Reject
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="approval-summary-header approval-summary-header-mobile">
          {/* Row 1: Title */}
          <div className="approval-summary-mobile-row-1">
            <h3 className="approval-summary-title">Forms Awaiting Approval</h3>
          </div>

          {/* Row 2: Search Bar */}
          {projectId === PROJECT_IDS.EQUIPMENT_YARD && (
            <div className="approval-summary-mobile-row-2">
              <div className="approval-summary-search">
                <input
                  type="text"
                  className="approval-summary-search-input"
                  placeholder="Search by Asset or Location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="approval-summary-search-clear"
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Row 3: Stats + Bulk Action Buttons */}
          <div className="approval-summary-mobile-row-3">
            <div className="approval-summary-stats">
              <div>In Review: <strong>{stats.pendingApproval}</strong></div>
              {searchQuery && projectId === PROJECT_IDS.EQUIPMENT_YARD && (
                <div>Filtered: <strong>{filteredReports.length}</strong></div>
              )}
            </div>

            {!isCostManagementProject(projectId) && !isCostModuleView(projectId) && hasAnyReviewerAccess && (
              <div className="approval-summary-mobile-buttons">
                <Button
                  variant="blue"
                  icon="✓"
                  onClick={bulkApprove}
                  disabled={Object.keys(selectedMap).length === 0}
                >
                  Bulk Approve ({Object.keys(selectedMap).length})
                </Button>

                <Button
                  variant="red"
                  icon="✕"
                  onClick={bulkReject}
                  disabled={Object.keys(selectedMap).length === 0}
                >
                  Bulk Reject
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Show loading state - hide tables during initial load */}
        {loading && pendingReports.length === 0 ? (
          <div>
            {/* Show progress message */}
            {isCostModuleView(projectId) && allProjectsLoadingProgress?.total > 0 && (
              <div style={{
                textAlign: 'center',
                padding: '1rem',
                color: '#64748b',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                Loading projects: {allProjectsLoadingProgress.loaded}/{allProjectsLoadingProgress.total}
              </div>
            )}
            {/* Skeleton loader for table */}
            <SkeletonLoader rows={8} columns={6} type="table" />
          </div>
        ) : (
          <>
        {/* Render template-specific tables for Equipment Yard with multiple templates */}
        {Object.keys(templateFieldDefs).length > 0 ? (
          <>
            {Object.keys(templateFieldDefs).map(tid => {
              const templateDef = templateFieldDefs[tid]
              const templateReports = filteredReports.filter(r => r._templateId === tid)

              if (templateReports.length === 0) return null

              const templateConfig = TEMPLATE_FIELD_CONFIGS[tid]

              const permissions = templatePermissions[tid] || {
                canView: true,
                canEdit: false,
                canApprove: false,
                canReject: false,
                hasReviewerAccess: false
              }

              return (
                <TemplateTable
                  key={tid}
                  templateId={tid}
                  templateName={templateDef.templateName}
                  fieldDefinitions={templateDef}
                  templateConfig={templateConfig}
                  reports={templateReports}
                  projectId={projectId}
                  permissions={permissions}
                  sortKey={sortKey}
                  sortDir={sortDir}
                  handleSort={handleSort}
                  selectedMap={selectedMap}
                  toggleSelect={toggleSelect}
                  toggleSelectAll={toggleSelectAllForReports}
                  editingRow={editingRow}
                  editedValues={editedValues}
                  handleFieldChange={handleFieldChange}
                  rowLoading={rowLoading}
                  rowAction={rowAction}
                  handleApproveRow={handleApproveRow}
                  handleRejectRow={handleRejectRow}
                  handleEditRow={handleEditRow}
                  handleSaveEdit={handleSaveEdit}
                  handleCancelEdit={handleCancelEdit}
                  columnWidths={columnWidths}
                  handleResizeStart={handleResizeStart}
                  locationMap={locationMap}
                  locationsList={locationsList}
                  locationDropdownOpen={locationDropdownOpen}
                  setLocationDropdownOpen={setLocationDropdownOpen}
                  expandedLocations={expandedLocations}
                  setExpandedLocations={setExpandedLocations}
                  locationTriggerRef={locationTriggerRef}
                  assetMap={assetMap}
                  formAssetMap={formAssetMap}
                />
              )
            })}
          </>
        ) : (
          // Original single table for other projects
          <>
          <div className="approval-summary-table-wrap" ref={tableRef}>
          <table className={`approval-summary-table ${isHeaderSticky ? 'header-sticky' : ''}`} role="table">
            <thead ref={theadRef} className={isHeaderSticky ? 'sticky' : ''}>
              <tr>
                {/* Select all checkbox - show for Equipment Yard and Hocon Sandbox */}
                {(projectId === PROJECT_IDS.EQUIPMENT_YARD || projectId === PROJECT_IDS.HOCON_SANDBOX) && hasAnyReviewerAccess && (
                  <th className="approval-summary-table">
                    <input
                      type="checkbox"
                      aria-label="Select all forms"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                    />
                  </th>
                )}

                {isCostManagementProj ? (
                  // Headers for Cost Management projects (Contracts & Subcontracts)
                  <>
                    <ResizableHeader
                      columnKey="code"
                      style={{width: '120px', cursor: 'pointer'}}
                      onClick={() => handleSort('code')}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Code {sortKey === 'code' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="_workflowType"
                      style={{width: '110px', cursor: 'pointer'}}
                      onClick={() => handleSort('_workflowType')}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Type {sortKey === '_workflowType' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="name"
                      style={{minWidth: '200px', cursor: 'pointer'}}
                      onClick={() => handleSort('name')}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Name {sortKey === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="tradePartner"
                      style={{minWidth: '180px', cursor: 'pointer'}}
                      onClick={() => handleSort('tradePartner')}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Trade Partner {sortKey === 'tradePartner' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="status"
                      style={{minWidth: '150px', cursor: 'pointer'}}
                      onClick={() => handleSort('status')}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Status {sortKey === 'status' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="compliance"
                      style={{minWidth: '140px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Compliance
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="attachments"
                      style={{minWidth: '200px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Attachments
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="packages"
                      style={{minWidth: '200px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Packages
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="timeline"
                      style={{minWidth: '250px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Workflow History
                    </ResizableHeader>
                  </>
                ) : isCostModuleView(projectId) ? (
                  // Headers for Cost Module view (same as Cost Management + Project column)
                  <>
                    <ResizableHeader
                      columnKey="projectName"
                      style={{minWidth: '180px', cursor: 'pointer'}}
                      onClick={() => handleSort('projectName')}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Project {sortKey === 'projectName' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="code"
                      style={{width: '120px', cursor: 'pointer'}}
                      onClick={() => handleSort('code')}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Code {sortKey === 'code' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="_workflowType"
                      style={{width: '110px', cursor: 'pointer'}}
                      onClick={() => handleSort('_workflowType')}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Type {sortKey === '_workflowType' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="name"
                      style={{minWidth: '200px', cursor: 'pointer'}}
                      onClick={() => handleSort('name')}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Name {sortKey === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="tradePartner"
                      style={{minWidth: '180px', cursor: 'pointer'}}
                      onClick={() => handleSort('tradePartner')}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Trade Partner {sortKey === 'tradePartner' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="attachments"
                      style={{minWidth: '200px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Attachments
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="packages"
                      style={{minWidth: '200px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Packages
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="timeline"
                      style={{minWidth: '250px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Workflow History
                    </ResizableHeader>
                  </>
                ) : projectId === PROJECT_IDS.HOCON_SANDBOX ? (
                  // Headers for Hocon Sandbox project (Forms)
                  <>
                    <ResizableHeader
                      columnKey="formName"
                      style={{minWidth: '180px', cursor: 'pointer'}}
                      onClick={() => handleSort('name')}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Form name {sortKey === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="template"
                      style={{minWidth: '150px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Template
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="description"
                      style={{minWidth: '180px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Description
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="formDate"
                      style={{width: '120px', cursor: 'pointer'}}
                      onClick={() => handleSort('formDate')}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Form date {sortKey === 'formDate' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="formNum"
                      style={{width: '80px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      ID
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="status"
                      style={{minWidth: '120px', cursor: 'pointer'}}
                      onClick={() => handleSort('status')}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Status {sortKey === 'status' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="location"
                      style={{minWidth: '150px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Location
                    </ResizableHeader>
                  </>
                ) : (
                  // Default headers for fuel log projects
                  <>
                    <ResizableHeader
                      columnKey="formNum"
                      style={{width: '80px', cursor: 'pointer'}}
                      onClick={() => handleSort('formNum')}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Form # {sortKey === 'formNum' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="formName"
                      style={{minWidth: '180px', cursor: 'pointer'}}
                      onClick={() => handleSort('name')}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Form Name {sortKey === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="date"
                      style={{width: '120px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Date
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="operatorName"
                      style={{minWidth: '140px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Operator Name
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="jobNumber"
                      style={{width: '130px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Job #
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="equipmentNumber"
                      style={{width: '140px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Equipment #
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="fuelType"
                      style={{width: '140px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Fuel Type
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="quantity"
                      style={{width: '120px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Quantity
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="startReading"
                      style={{width: '130px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Start Reading
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="endReading"
                      style={{width: '130px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      End Reading
                    </ResizableHeader>

                    <ResizableHeader
                      columnKey="location"
                      style={{minWidth: '150px'}}
                      columnWidths={columnWidths}
                      onResizeStart={handleResizeStart}
                    >
                      Location
                    </ResizableHeader>
                  </>
                )}

                {/* View in ACC column */}
                <ResizableHeader
                  columnKey="viewInACC"
                  style={{minWidth: '120px', textAlign: 'center'}}
                  columnWidths={columnWidths}
                  onResizeStart={handleResizeStart}
                >
                  View in ACC
                </ResizableHeader>

                {/* Actions column - hide for Cost Module view, Cost Management projects, and Hocon Sandbox */}
                {!isCostModuleView(projectId) && !isCostManagementProj && projectId !== PROJECT_IDS.HOCON_SANDBOX && (
                  <th className="approval-summary-table" style={{
                    minWidth: columnWidths.actions || '310px',
                    width: columnWidths.actions || '310px',
                    position: 'sticky',
                    right: 0,
                    backgroundColor: '#f8fafc',
                    boxShadow: '-2px 0 4px rgba(0, 0, 0, 0.05)',
                    zIndex: 2
                  }} role="columnheader">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredReports.length === 0 && !loading && (
                <tr><td className="approval-summary-table" colSpan={isCostManagementProj ? 12 : 13}>
                  {searchQuery ? 'No forms match your search.' : 'No forms awaiting approval.'}
                </td></tr>
              )}

              {filteredReports.map((report) => {
                const isLoading = !!rowLoading[report.id]
                const errorMsg = rowError[report.id]
                const successMsg = rowSuccess[report.id]
                const isSelected = !!selectedMap[report.id]
                const isEditing = editingRow === report.id

                // Check if this contract is waiting for current user's approval
                const pendingTasks = contractPendingApprovals[report.id] || []
                const isWaitingForMe = pendingTasks.some(task => {
                  const stepName = task.stepName || ''
                  const assigneeName = task.assignee?.name || ''
                  const assigneeEmail = task.assignee?.email || ''
                  const userName = user?.name || user?.displayName || ''
                  const userEmail = user?.email || ''
                  return stepName.toLowerCase().includes(userName.toLowerCase()) ||
                         assigneeName.toLowerCase().includes(userName.toLowerCase()) ||
                         assigneeEmail.toLowerCase() === userEmail.toLowerCase()
                })

                return (
                  <tr
                    key={report.id}
                    style={{
                      backgroundColor: isWaitingForMe ? '#eff6ff' : '#fff',
                      borderLeft: isWaitingForMe ? '4px solid #3b82f6' : 'none'
                    }}
                  >
                    {/* row checkbox - show for Equipment Yard and Hocon Sandbox */}
                    {(projectId === PROJECT_IDS.EQUIPMENT_YARD || projectId === PROJECT_IDS.HOCON_SANDBOX) && hasAnyReviewerAccess && (
                      <td className="approval-summary-table">
                        <input
                          type="checkbox"
                          aria-label={`Select form ${report.formNum || report.id}`}
                          checked={isSelected}
                          onChange={() => toggleSelect(report.id)}
                          disabled={isEditing}
                        />
                      </td>
                    )}

                    {isCostManagementProj ? (
                      // Render contract/subcontract-specific columns for Cost Management projects
                      <>
                        <td style={{fontWeight: 700, color: '#1e40af', fontSize: '0.95rem'}} title={report.code ?? '—'}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span className="approval-summary-td-content">{report.code ?? '—'}</span>
                            {isWaitingForMe && (
                              <span style={{
                                background: '#3b82f6',
                                color: '#fff',
                                padding: '1px 6px',
                                borderRadius: 10,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                boxShadow: '0 1px 2px rgba(59, 130, 246, 0.3)',
                                animation: 'pulse 2s infinite'
                              }}>
                                YOUR APPROVAL
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Workflow Type Column */}
                        <td style={{fontSize: '0.85rem'}} title={report._workflowType ?? 'Unknown'}>
                          <span className="approval-summary-td-content" style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            backgroundColor: report._isContract ? '#dbeafe' : '#fef3c7',
                            color: report._isContract ? '#1e40af' : '#92400e'
                          }}>
                            {report._workflowType ?? 'Unknown'}
                          </span>
                        </td>

                        <td className="approval-summary-table" title={report.name ?? '—'}>
                          <span className="approval-summary-td-content">{report.name ?? '—'}</span>
                        </td>

                        <td className="approval-summary-table" title={report.tradePartner ?? report.supplier ?? '—'}>
                          <span className="approval-summary-td-content">{report.tradePartner ?? report.supplier ?? '—'}</span>
                        </td>

                        <td className="approval-summary-table" title={report.status ?? '—'}>
                          <span style={{
                            
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: 4,
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            backgroundColor: report.status?.toLowerCase().includes('review') ? '#fef3c7' : '#e0e7ff',
                            color: report.status?.toLowerCase().includes('review') ? '#92400e' : '#3730a3'
                          }}>
                            {report.status ?? '—'}
                          </span>
                        </td>

                        <td className="approval-summary-table">
                          <ComplianceStatus
                            status={report.compliance}
                            details={report.complianceDetails}
                          />
                        </td>

                        {/* Attachments Column */}
                        <td className="approval-summary-table">
                          <AttachmentsColumn
                            attachments={contractAttachments[report.id] || []}
                            downloadingItems={downloadingAttachments}
                            onPreview={handleOpenAttachment}
                            onDownload={handleDownloadAttachment}
                          />
                        </td>

                        {/* Packages Column */}
                        <td className="approval-summary-table">
                          <PackagesColumn
                            packages={contractPackages[report.id] || []}
                            downloadingItems={downloadingAttachments}
                            onDownload={handleDownloadAttachment}
                          />
                        </td>

                        {/* Workflow History Timeline Column */}
                        <td className="approval-summary-table">
                          <WorkflowHistory
                            history={contractWorkflowHistory[report.id] || []}
                            pendingTasks={contractPendingApprovals[report.id] || []}
                            currentUser={user}
                          />
                        </td>
                      </>
                    ) : isCostModuleView(projectId) ? (
                      // Render contract/subcontract columns for Cost Module (same as Cost Management + Project Name)
                      <>
                        {/* Project Name Column */}
                        <td className="approval-summary-table" title={report.projectName ?? report._projectName ?? '—'}>
                          <span className="approval-summary-td-content" style={{
                            fontWeight: 600,
                            color: '#059669',
                            fontSize: '0.9rem'
                          }}>
                            {report.projectName ?? report._projectName ?? '—'}
                          </span>
                        </td>

                        <td style={{fontWeight: 700, color: '#1e40af', fontSize: '0.95rem'}} title={report.code ?? '—'}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span className="approval-summary-td-content">{report.code ?? '—'}</span>
                            {isWaitingForMe && (
                              <span style={{
                                background: '#3b82f6',
                                color: '#fff',
                                padding: '1px 6px',
                                borderRadius: 10,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                boxShadow: '0 1px 2px rgba(59, 130, 246, 0.3)',
                                animation: 'pulse 2s infinite'
                              }}>
                                YOUR APPROVAL
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Workflow Type Column */}
                        <td style={{fontSize: '0.85rem'}} title={report._workflowType ?? 'Unknown'}>
                          <span className="approval-summary-td-content" style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            backgroundColor: report._isContract ? '#dbeafe' : '#fef3c7',
                            color: report._isContract ? '#1e40af' : '#92400e'
                          }}>
                            {report._workflowType ?? 'Unknown'}
                          </span>
                        </td>

                        <td className="approval-summary-table" title={report.name ?? '—'}>
                          <span className="approval-summary-td-content">{report.name ?? '—'}</span>
                        </td>

                        <td className="approval-summary-table" title={report.tradePartner ?? report.supplier ?? '—'}>
                          <span className="approval-summary-td-content">{report.tradePartner ?? report.supplier ?? '—'}</span>
                        </td>

                        {/* Attachments Column */}
                        <td className="approval-summary-table">
                          <AttachmentsColumn
                            attachments={contractAttachments[report.id] || []}
                            downloadingItems={downloadingAttachments}
                            onPreview={handleOpenAttachment}
                            onDownload={handleDownloadAttachment}
                          />
                        </td>

                        {/* Packages Column */}
                        <td className="approval-summary-table">
                          <PackagesColumn
                            packages={contractPackages[report.id] || []}
                            downloadingItems={downloadingAttachments}
                            onDownload={handleDownloadAttachment}
                          />
                        </td>

                        {/* Workflow History Timeline Column */}
                        <td className="approval-summary-table">
                          <WorkflowHistory
                            history={contractWorkflowHistory[report.id] || []}
                            pendingTasks={contractPendingApprovals[report.id] || []}
                            currentUser={user}
                          />
                        </td>
                      </>
                    ) : projectId === PROJECT_IDS.HOCON_SANDBOX ? (
                      // Render form columns for Hocon Sandbox
                      <>
                        {/* Form name */}
                        <td style={{fontWeight: 700, color: '#1e40af', fontSize: '0.95rem'}} title={report.name ?? '—'}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span className="approval-summary-td-content">{report.name ?? '—'}</span>
                          </div>
                        </td>

                        {/* Template */}
                        <td className="approval-summary-table" title={report.name ?? '—'}>
                          <span className="approval-summary-td-content">
                            {report.name ?? '—'}
                          </span>
                        </td>

                        {/* Description */}
                        <td className="approval-summary-table" title={report.description || '—'}>
                          <span className="approval-summary-td-content">{report.description || '—'}</span>
                        </td>

                        {/* Form date */}
                        <td className="approval-summary-table" title={report.formDate || '—'}>
                          <span className="approval-summary-td-content">
                            {report.formDate ? new Date(report.formDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            }) : '—'}
                          </span>
                        </td>

                        {/* ID (formNum) */}
                        <td className="approval-summary-table" title={report.formNum ?? '—'}>
                          <span className="approval-summary-td-content">{report.formNum ?? '—'}</span>
                        </td>

                        {/* Status */}
                        <td className="approval-summary-table" title={report.status ?? '—'}>
                          <span style={{

                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: 4,
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            backgroundColor: report.status?.toLowerCase().includes('review') ? '#fef3c7' :
                                           report.status?.toLowerCase().includes('progress') ? '#dbeafe' : '#e0e7ff',
                            color: report.status?.toLowerCase().includes('review') ? '#92400e' :
                                   report.status?.toLowerCase().includes('progress') ? '#1e40af' : '#3730a3'
                          }}>
                            {report.status ?? '—'}
                          </span>
                        </td>

                        {/* Location */}
                        <td className="approval-summary-table" title={(() => {
                          // Contracts and subcontracts don't have location data
                          if (report._isContract || report._isSubcontract) return 'N/A'

                          let locationValue = '—'
                          if (report.locationId && locationMap[report.locationId]) {
                            locationValue = locationMap[report.locationId]
                          } else if (report.location?.name) {
                            locationValue = report.location.name
                          } else if (report.locationName) {
                            locationValue = report.locationName
                          } else if (report.locationPath) {
                            locationValue = report.locationPath
                          }
                          return locationValue
                        })()}>
                          <span className="approval-summary-td-content">{(() => {
                            // Contracts and subcontracts don't have location data
                            if (report._isContract || report._isSubcontract) return 'N/A'

                            let locationValue = '—'
                            if (report.locationId && locationMap[report.locationId]) {
                              locationValue = locationMap[report.locationId]
                            } else if (report.location?.name) {
                              locationValue = report.location.name
                            } else if (report.locationName) {
                              locationValue = report.locationName
                            } else if (report.locationPath) {
                              locationValue = report.locationPath
                            }
                            return locationValue
                          })()}</span>
                        </td>
                      </>
                    ) : projectId === PROJECT_IDS.HOCON_SANDBOX ? (
                      // Render form columns for Hocon Sandbox project
                      <>
                        {/* Form name */}
                        <td style={{fontWeight: 700, color: '#1e40af', fontSize: '0.95rem'}} title={report.name ?? '—'}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span className="approval-summary-td-content">{report.name ?? '—'}</span>
                          </div>
                        </td>

                        {/* Template */}
                        <td className="approval-summary-table" title={report.name ?? '—'}>
                          <span className="approval-summary-td-content">
                            {report.name ?? '—'}
                          </span>
                        </td>

                        {/* Description */}
                        <td className="approval-summary-table" title={report.description || '—'}>
                          <span className="approval-summary-td-content">{report.description || '—'}</span>
                        </td>

                        {/* Form date */}
                        <td className="approval-summary-table" title={report.formDate || '—'}>
                          <span className="approval-summary-td-content">
                            {report.formDate ? new Date(report.formDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            }) : '—'}
                          </span>
                        </td>

                        {/* ID (formNum) */}
                        <td className="approval-summary-table" title={report.formNum ?? '—'}>
                          <span className="approval-summary-td-content">{report.formNum ?? '—'}</span>
                        </td>

                        {/* Status */}
                        <td className="approval-summary-table" title={report.status ?? '—'}>
                          <span style={{
                            
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: 4,
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            backgroundColor: report.status?.toLowerCase().includes('review') ? '#fef3c7' :
                                           report.status?.toLowerCase().includes('progress') ? '#dbeafe' : '#e0e7ff',
                            color: report.status?.toLowerCase().includes('review') ? '#92400e' :
                                   report.status?.toLowerCase().includes('progress') ? '#1e40af' : '#3730a3'
                          }}>
                            {report.status ?? '—'}
                          </span>
                        </td>

                        {/* Location */}
                        <td className="approval-summary-table" title={(() => {
                          let locationValue = '—'
                          if (report.locationId && locationMap[report.locationId]) {
                            locationValue = locationMap[report.locationId]
                          } else if (report.location?.name) {
                            locationValue = report.location.name
                          } else if (report.locationName) {
                            locationValue = report.locationName
                          } else if (report.locationPath) {
                            locationValue = report.locationPath
                          }
                          return locationValue
                        })()}>
                          <span className="approval-summary-td-content">{(() => {
                            let locationValue = '—'
                            if (report.locationId && locationMap[report.locationId]) {
                              locationValue = locationMap[report.locationId]
                            } else if (report.location?.name) {
                              locationValue = report.location.name
                            } else if (report.locationName) {
                              locationValue = report.locationName
                            } else if (report.locationPath) {
                              locationValue = report.locationPath
                            }
                            return locationValue
                          })()}</span>
                        </td>
                      </>
                    ) : (
                      // Render fuel log columns for other projects
                      <>
                        <td style={{fontWeight: 700, color: '#1e40af', fontSize: '0.95rem'}} title={report.formNum ?? '—'}>
                          <span className="approval-summary-td-content">{report.formNum ?? '—'}</span>
                        </td>

                        <td className="approval-summary-table">
                          {(() => {
                            const name = report.name || report.title || 'Untitled'
                            const displayName = name.replace(/Service Station Fuel Log/gi, '').trim() || 'Form'
                            const description = report.description || ''
                            const fullText = description ? `${displayName}\n${description}` : displayName

                            return (
                              <div title={fullText}>
                                <div style={{  fontWeight: 600, color: '#1e293b', fontSize: '0.95rem', marginBottom: '4px' }}>
                                  {displayName}
                                </div>
                                {description && (
                                  <div className="as-text-muted-italic">
                                    {description}
                                  </div>
                                )}
                              </div>
                            )
                          })()}
                        </td>

                        {/* Individual field columns */}
                        <FuelLogFieldColumns
                          report={report}
                          isEditing={isEditing}
                          editedValues={editedValues}
                          handleFieldChange={handleFieldChange}
                          FIELD_IDS={FIELD_IDS}
                        />

                        <td style={{  position: 'relative' }}>
                          {isEditing ? (
                            <div style={{ position: 'relative' }}>
                              <div
                                ref={locationTriggerRef}
                                onClick={() => setLocationDropdownOpen(!locationDropdownOpen)}
                                style={{
                                  width: '100%',
                                  padding: '6px 8px',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: 4,
                                  fontSize: '0.9rem',
                                  backgroundColor: '#fff',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                              >
                                <span style={{ color: editedValues.locationId ? '#334155' : '#94a3b8' }}>
                                  {editedValues.locationId && locationMap[editedValues.locationId]
                                    ? locationMap[editedValues.locationId]
                                    : 'Select Location'}
                                </span>
                                <span className="as-text-muted-small">
                                  {locationDropdownOpen ? '▲' : '▼'}
                                </span>
                              </div>
                              {locationDropdownOpen && (
                                <LocationDropdown
                                  value={editedValues.locationId || ''}
                                  onChange={(locId) => {
                                    handleFieldChange('locationId', locId)
                                    setLocationDropdownOpen(false)
                                  }}
                                  locations={locationsList}
                                  expandedLocations={expandedLocations}
                                  setExpandedLocations={setExpandedLocations}
                                  onClose={() => setLocationDropdownOpen(false)}
                                  triggerRef={locationTriggerRef}
                                />
                              )}
                            </div>
                          ) : (
                            (() => {
                              let locationValue = '—'

                              // First try to get locationId and map it to location name
                              if (report.locationId && locationMap[report.locationId]) {
                                locationValue = locationMap[report.locationId]
                              }
                              // Fallback: Try multiple ways to get location directly
                              else if (report.location?.name) locationValue = report.location.name
                              else if (report.locationName) locationValue = report.locationName
                              else if (report.locationPath) locationValue = report.locationPath
                              else {
                                // Try to find location in customValues
                                const locationField = report.nativeForm?.customValues?.find(
                                  field => field.definition?.name?.toLowerCase().includes('location') ||
                                           field.definition?.label?.toLowerCase().includes('location')
                                )
                                if (locationField?.value) locationValue = locationField.value
                              }

                              return <span className="approval-summary-td-content" title={locationValue}>{locationValue}</span>
                            })()
                          )}
                        </td>
                      </>
                    )}

                    {/* View in ACC column */}
                    <td style={{ textAlign: 'center', minWidth: '120px' }}>
                      <ViewInACCButton report={report} projectId={projectId} templateId={templateId} />
                    </td>

                    {/* Actions column - sticky to right - hide for Cost Module view, Cost Management projects, and Hocon Sandbox */}
                    {!isCostModuleView(projectId) && !isCostManagementProj && projectId !== PROJECT_IDS.HOCON_SANDBOX && (
                      <ActionsColumn
                        report={report}
                        isEditing={isEditing}
                        isLoading={isLoading}
                        currentAction={rowAction[report.id]}
                        isAnyRowEditing={editingRow !== null}
                        isAnyCheckboxSelected={isAnyCheckboxSelected}
                        isThisRowSelected={isSelected}
                        selectedCount={selectedCount}
                        showEditButton={!isCostManagementProj}
                        onSave={handleSaveEdit}
                        onCancel={handleCancelEdit}
                        onEdit={handleEditRow}
                        onApprove={handleApproveRow}
                        onReject={handleRejectRow}
                      />
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards View for All Approvals - Hidden on desktop, shown on mobile */}
        <div className="mobile-forms-cards">
          {filteredReports.length === 0 && !loading ? (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              color: '#64748b',
              fontSize: '0.9rem',
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              margin: '1rem'
            }}>
              No forms awaiting approval.
            </div>
          ) : filteredReports.length === 0 && loading ? (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              color: '#64748b',
              fontSize: '0.9rem'
            }}>
              <SkeletonLoader />
            </div>
          ) : (
            filteredReports.map((report) => (
              <div key={report.id} className="mobile-form-card">
                {/* Card Header */}
                <div className="mobile-form-card-header">
                  <h4 className="mobile-form-card-title">
                    {report.formName || report.name || report.code || 'Form'}
                  </h4>
                  {(projectId === PROJECT_IDS.EQUIPMENT_YARD || projectId === PROJECT_IDS.HOCON_SANDBOX) && hasAnyReviewerAccess && (
                    <input
                      type="checkbox"
                      className="mobile-form-card-checkbox"
                      aria-label={`Select form ${report.formNum || report.id}`}
                      checked={!!selectedMap[report.id]}
                      onChange={() => toggleSelect(report.id)}
                    />
                  )}
                </div>

                {/* Card Body - Show ALL fields from table */}
                <div className="mobile-form-card-body">
                  {isCostModuleView(projectId) || isCostManagementProject(projectId) ? (
                    // Cost Module / Cost Management - Contract/Subcontract fields
                    <>
                      {/* Project Name (Cost Module only) */}
                      {isCostModuleView(projectId) && (report.projectName || report._projectName) && (
                        <div className="mobile-form-field">
                          <div className="mobile-form-field-label">Project</div>
                          <div className="mobile-form-field-value">{report.projectName || report._projectName}</div>
                        </div>
                      )}

                      {/* Code */}
                      <div className="mobile-form-field">
                        <div className="mobile-form-field-label">Code</div>
                        <div className="mobile-form-field-value">{report.code || '—'}</div>
                      </div>

                      {/* Type */}
                      <div className="mobile-form-field">
                        <div className="mobile-form-field-label">Type</div>
                        <div className="mobile-form-field-value">
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            backgroundColor: report._isContract ? '#dbeafe' : '#fef3c7',
                            color: report._isContract ? '#1e40af' : '#92400e'
                          }}>
                            {report._workflowType || 'Unknown'}
                          </span>
                        </div>
                      </div>

                      {/* Name */}
                      <div className="mobile-form-field">
                        <div className="mobile-form-field-label">Name</div>
                        <div className="mobile-form-field-value">{report.name || '—'}</div>
                      </div>

                      {/* Trade Partner */}
                      <div className="mobile-form-field">
                        <div className="mobile-form-field-label">Trade Partner</div>
                        <div className="mobile-form-field-value">{report.tradePartner || report.supplier || '—'}</div>
                      </div>

                      {/* Attachments */}
                      <div className="mobile-form-field">
                        <div className="mobile-form-field-label">Attachments</div>
                        <div className="mobile-form-field-value">
                          <AttachmentsColumn
                            attachments={contractAttachments[report.id] || []}
                            downloadingItems={downloadingAttachments}
                            onPreview={handleOpenAttachment}
                            onDownload={handleDownloadAttachment}
                          />
                        </div>
                      </div>

                      {/* Packages */}
                      <div className="mobile-form-field">
                        <div className="mobile-form-field-label">Packages</div>
                        <div className="mobile-form-field-value">
                          <PackagesColumn
                            packages={contractPackages[report.id] || []}
                            downloadingItems={downloadingAttachments}
                            onDownload={handleDownloadAttachment}
                          />
                        </div>
                      </div>

                      {/* Workflow History */}
                      <div className="mobile-form-field">
                        <div className="mobile-form-field-label">Workflow History</div>
                        <div className="mobile-form-field-value">
                          <WorkflowHistory
                            history={contractWorkflowHistory[report.id] || []}
                            pendingTasks={contractPendingApprovals[report.id] || []}
                            currentUser={user}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    // Equipment Yard / Hocon Sandbox - Form fields
                    <>
                      {/* Project */}
                      {report.proj && (
                        <div className="mobile-form-field">
                          <div className="mobile-form-field-label">Project</div>
                          <div className="mobile-form-field-value">{report.proj}</div>
                        </div>
                      )}

                      {/* Form Name */}
                      {report.formName && (
                        <div className="mobile-form-field">
                          <div className="mobile-form-field-label">Form Name</div>
                          <div className="mobile-form-field-value">{report.formName}</div>
                        </div>
                      )}

                      {/* Template */}
                      {report.templateName && (
                        <div className="mobile-form-field">
                          <div className="mobile-form-field-label">Template</div>
                          <div className="mobile-form-field-value">{report.templateName}</div>
                        </div>
                      )}

                      {/* Description */}
                      <div className="mobile-form-field">
                        <div className="mobile-form-field-label">Description</div>
                        <div className="mobile-form-field-value">{report.description || '—'}</div>
                      </div>

                      {/* Form Date */}
                      <div className="mobile-form-field">
                        <div className="mobile-form-field-label">Form Date</div>
                        <div className="mobile-form-field-value">
                          {report.formDate ? new Date(report.formDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          }) : '—'}
                        </div>
                      </div>

                      {/* Form Number / ID */}
                      <div className="mobile-form-field">
                        <div className="mobile-form-field-label">ID</div>
                        <div className="mobile-form-field-value">{report.formNum || report.id || '—'}</div>
                      </div>

                      {/* Trade */}
                      <div className="mobile-form-field">
                        <div className="mobile-form-field-label">Trade</div>
                        <div className="mobile-form-field-value">{report.trade || '—'}</div>
                      </div>
                    </>
                  )}
                </div>

                {/* Card Actions - View in ACC Button */}
                <div className="mobile-form-card-actions">
                  <ViewInACCButton report={report} projectId={projectId} templateId={templateId} />
                </div>
              </div>
            ))
          )}
        </div>
        </>
        )}
        </>
        )}

      </div>

      {/* Success modal */}
      <SuccessModal
        isOpen={successModal.isOpen}
        message={successModal.message}
        type={successModal.type}
        onClose={successModal.close}
      />

      {/* Confirm modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        onConfirm={confirmModal.handleConfirm}
        onCancel={confirmModal.handleCancel}
      />

      {/* Loading overlay - Hide for All Projects and Cost Module views (use LoadingIndicator instead) */}
      <LoadingOverlay
        show={!isCostModuleView(projectId) && (loading || Object.values(rowLoading).some(val => val))}
        message={loading ? 'Refreshing' : 'Processing...'}
        position="bottom-right"
      />

      {/* Attachment Modal */}
      <AttachmentModal
        show={showAttachmentModal}
        attachment={selectedAttachment}
        onClose={handleCloseAttachmentModal}
      />

      {/* Loading indicator for All Projects at bottom right */}
      <LoadingIndicator
        message={
          allProjectsLoadingProgress?.total > 0
            ? `Loading projects: ${allProjectsLoadingProgress.loaded}/${allProjectsLoadingProgress.total}`
            : "Refreshing"
        }
        show={
          isCostModuleView(projectId) &&
          (loading || loadingLocations) &&
          pendingReports.length === 0
        }
      />
    </div>
  )
}

export default ApprovalSummaryTable
